import React, { useState, useEffect } from "react";
import { X, FileEdit, UserCheck, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import type { OrderDetailsResponse, DoctorListItem, LabTestOption, MedicineEntry, Medication } from "../../services/api/admin-order.types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import ConsultationForm from "./ConsultationForm";
import DoctorAssignFlow from "./DoctorAssignFlow";

interface GeneratePrescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number;
  orderDetails?: OrderDetailsResponse | null;
  onSuccess?: () => void;
}

const GeneratePrescriptionModal: React.FC<GeneratePrescriptionModalProps> = ({
  isOpen,
  onClose,
  orderId,
  orderDetails: initialOrderDetails,
  onSuccess,
}) => {
  const [selectedTab, setSelectedTab] = useState<"self" | "doctor">("self");
  const [generating, setGenerating] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse | null>(initialOrderDetails || null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialOrderDetails) {
        setOrderDetails(initialOrderDetails);
      } else {
        fetchOrderDetails();
      }
    }
  }, [isOpen, initialOrderDetails, orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoadingDetails(true);
      const res = await AdminOrderManagementService.getOrderDetails(orderId);
      setOrderDetails(res);
    } catch (err) {
      console.error("Error fetching order details for modal:", err);
      toast.error("Failed to load order details");
    } finally {
      setLoadingDetails(false);
    }
  };

  if (!isOpen) return null;

  const handleGeneratePrescription = async (data: {
    doctor: DoctorListItem;
    chiefComplaints: string;
    diagnosis: string;
    labTests: LabTestOption[];
    medicines: MedicineEntry[];
    specialInstructions: string;
  }) => {
    if (!orderDetails) {
      toast.error("Order details not loaded yet");
      return;
    }

    try {
      setGenerating(true);
      const now = new Date();
      const medications: Medication[] = data.medicines.map((m, i) => ({
        medicineName: m.medicineName,
        medicineId: m.medicineId,
        medicineSalt: m.medicineSalt,
        dosageType: m.dosageType,
        medicineNote: m.note,
        sequenceNo: i + 1,
        originalText: m.originalText,
        generatedText: m.generatedText,
        isTapering: false,
      }));

      const payload = {
        orderId: orderDetails.orderId,
        prescriptionType: "DOCTOR_GENERATED",
        doctor: {
          name: data.doctor.name,
          specializations: data.doctor.doctorSpecialization || "",
          eductation: data.doctor.doctorEducation || "",
          regNo: data.doctor.registrationNumber || "",
          phone: "",
          address: data.doctor.address || "",
        },
        patient: {
          name: orderDetails.orderedForName || orderDetails.patientName || "",
          mobile: orderDetails.orderedForPhone || orderDetails.patientPhone || "",
          patientAge: orderDetails.orderedForAge || orderDetails.patientAge || "",
          patientGender: orderDetails.orderedForGender || orderDetails.patientGender || "",
        },
        appointment: {
          appointmentId: orderDetails.orderNumber,
          appointmentDate: now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
          appointmentTime: now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
          dhrId: orderDetails.orderNumber,
        },
        chiefComplaints: data.chiefComplaints,
        diagnosis: data.diagnosis,
        testsPrescribed: data.labTests.map((t) => t.name),
        medications,
        specialInstructions: data.specialInstructions,
        note: "Medical substitution allowed whenever applicable",
        prescribedOn: now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
      };

      const response = await AdminOrderManagementService.generatePrescription(payload);
      if (response.success) {
        toast.success("Prescription generated successfully!");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        toast.error(response.message || "Failed to generate prescription");
      }
    } catch (error) {
      console.error("Generate Rx Error:", error);
      toast.error("Failed to generate prescription");
    } finally {
      setGenerating(false);
    }
  };

  const handleDoctorAssignSuccess = () => {
    onClose();
    if (onSuccess) onSuccess();
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[950] flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-[760px] w-full max-h-[90vh] flex flex-col shadow-2xl animate-slideUp overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/50">
          <div>
            <h3 className="text-base font-bold text-slate-800">Generate Prescription</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Generate prescription for Order #{orderDetails?.orderNumber || orderId}
            </p>
          </div>
          <button
            type="button"
            className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs Bar */}
        <div className="px-6 pt-4 pb-2 bg-slate-50/30 border-b border-slate-100">
          <div className="flex rounded-xl bg-slate-200/80 p-1">
            <button
              type="button"
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all border-none cursor-pointer flex items-center justify-center gap-2 ${
                selectedTab === "self" ? "bg-white text-slate-800 shadow-sm font-bold" : "bg-transparent text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setSelectedTab("self")}
            >
              <FileEdit size={14} />
              Generate Prescription By Yourself
            </button>
            <button
              type="button"
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all border-none cursor-pointer flex items-center justify-center gap-2 ${
                selectedTab === "doctor" ? "bg-white text-slate-800 shadow-sm font-bold" : "bg-transparent text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setSelectedTab("doctor")}
            >
              <UserCheck size={14} />
              Generate Prescription By Doctor
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {loadingDetails ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 size={28} className="animate-spin text-emerald-500" />
              <span className="text-xs">Loading order details...</span>
            </div>
          ) : !orderDetails ? (
            <div className="text-center py-8 text-xs text-rose-500 font-medium">
              Failed to load order details. Please try again.
            </div>
          ) : selectedTab === "self" ? (
            <ConsultationForm
              onGeneratePrescription={handleGeneratePrescription}
              generating={generating}
            />
          ) : (
            <DoctorAssignFlow
              orderDetails={orderDetails}
              onSuccess={handleDoctorAssignSuccess}
            />
          )}
        </div>

        {/* Modal Footer (for Self tab action) */}
        {selectedTab === "self" && orderDetails && (
          <div className="px-6 py-3.5 border-t border-slate-200 flex justify-end gap-3 bg-slate-50/50">
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
              onClick={onClose}
              disabled={generating}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={generating}
              onClick={() => {
                const formTrigger = document.querySelector("[data-generate-trigger]") as HTMLButtonElement | null;
                if (formTrigger) {
                  formTrigger.click();
                }
              }}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-md transition-all border-none cursor-pointer disabled:opacity-50"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <FileEdit size={14} />}
              Generate Prescription
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratePrescriptionModal;
